import { ReactNode, useEffect, useRef } from 'react';
import {
  ActivityIndicator,
  Animated,
  Image,
  Keyboard,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  TextProps,
  View,
  ViewProps,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, fonts, gradients, minSafeArea, radii, shadows } from '../theme';

/** Color-dependent style fragments, recomputed on every render so a theme
 * toggle (which mutates `colors` in place, see theme.ts) is picked up —
 * unlike `StyleSheet.create`, whose values are baked in once at import
 * time. Structural (non-color) styles stay in the static `styles` below. */
function themed() {
  return {
    screen: { backgroundColor: colors.background },
    card: { backgroundColor: colors.card, borderColor: colors.border },
    gradientCard: { shadowColor: colors.primaryDark },
    buttonOutline: { borderColor: colors.primary },
    input: { borderColor: colors.border, color: colors.text, backgroundColor: colors.card },
    label: { color: colors.textMuted },
    error: { color: colors.danger },
    emptyStateIconWrap: { backgroundColor: colors.primarySoft },
    emptyStateText: { color: colors.textMuted },
    heading: { color: colors.text },
    title: { color: colors.text },
    body: { color: colors.text },
    caption: { color: colors.textMuted },
    checkboxRow: { borderColor: colors.border, backgroundColor: colors.card },
    checkboxRowChecked: { borderColor: colors.primary, backgroundColor: colors.primarySoft },
    checkboxBox: { borderColor: colors.textMuted },
    checkboxBoxChecked: { borderColor: colors.primary, backgroundColor: colors.primary },
    checkboxLabel: { color: colors.text },
    modalCard: { backgroundColor: colors.card },
    modalTitle: { color: colors.text },
    modalMessage: { color: colors.textMuted },
  };
}

/** The Loot coin badge — use anywhere a xu amount needs a visual icon
 * instead of (or alongside) the word "xu". The word "xu" itself stays in
 * copy/labels; this is purely the icon replacement. */
export function CoinIcon({ size = 20 }: { size?: number }) {
  return (
    <Image
      source={require('../../assets/coin-icon.png')}
      style={{ width: size, height: size }}
      resizeMode="contain"
    />
  );
}

interface ButtonProps {
  title: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'danger' | 'outline';
}

export function Button({ title, onPress, loading, disabled, variant = 'primary' }: ButtonProps) {
  const isDisabled = disabled || loading;
  const t = themed();
  // Dismissing here (rather than a screen-wide "tap outside" wrapper) means
  // every button press across the app closes the keyboard for free, without
  // ever intercepting/delaying a ScrollView's own touch handling — that
  // wrapper approach made scrolling feel unresponsive since the two were
  // competing to claim the same touch.
  const handlePress = () => {
    Keyboard.dismiss();
    onPress();
  };

  if (variant === 'primary') {
    return (
      <Pressable onPress={handlePress} disabled={isDisabled}>
        {({ pressed }) => (
          <LinearGradient
            colors={gradients.primary}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[
              styles.button,
              shadows.button,
              isDisabled && styles.buttonDisabled,
              pressed && !isDisabled && { opacity: 0.9 },
            ]}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.buttonText}>{title}</Text>
            )}
          </LinearGradient>
        )}
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={handlePress}
      disabled={isDisabled}
      style={({ pressed }) => [
        styles.button,
        variant === 'danger' && { backgroundColor: colors.danger },
        variant === 'outline' && [styles.buttonOutline, t.buttonOutline],
        isDisabled && styles.buttonDisabled,
        pressed && !isDisabled && { opacity: 0.85 },
      ]}
    >
      {loading ? (
        <ActivityIndicator color={variant === 'outline' ? colors.primary : '#fff'} />
      ) : (
        <Text style={[styles.buttonText, variant === 'outline' && { color: colors.primary }]}>
          {title}
        </Text>
      )}
    </Pressable>
  );
}

export function Card({ style, children, ...rest }: ViewProps) {
  const t = themed();
  return (
    <View style={[styles.card, t.card, style]} {...rest}>
      {children}
    </View>
  );
}

/** Full-bleed gradient card — for the one or two "hero" surfaces per screen
 * that should read as the primary focal point (balance, featured/sponsored
 * content). Overusing this defeats the point, so reach for the plain `Card`
 * everywhere else. */
export function GradientCard({
  style,
  children,
  colors: gradientColors = gradients.hero,
}: ViewProps & { colors?: readonly [string, string, ...string[]] }) {
  const t = themed();
  return (
    <LinearGradient
      colors={gradientColors}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[styles.card, styles.gradientCard, t.gradientCard, style]}
    >
      {children}
    </LinearGradient>
  );
}

/** Small circular icon badge — use as a leading visual on a card/section
 * title instead of an emoji, so it renders identically across every device
 * instead of depending on the platform's emoji font. */
export function IconBadge({
  name,
  size = 20,
  tone = 'primary',
}: {
  name: keyof typeof Ionicons.glyphMap;
  size?: number;
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'muted';
}) {
  const toneColors: Record<string, [string, string]> = {
    primary: [colors.primary, colors.primarySoft],
    success: [colors.success, '#DCFCE7'],
    warning: [colors.warning, '#FEF3C7'],
    danger: [colors.danger, '#FEE2E2'],
    muted: [colors.textMuted, colors.border],
  };
  const [fg, bg] = toneColors[tone];
  const box = size * 1.9;
  return (
    <View
      style={{
        width: box,
        height: box,
        borderRadius: box / 2,
        backgroundColor: bg,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Ionicons name={name} size={size} color={fg} />
    </View>
  );
}

/** Pulsing placeholder block — use in a list of these to build a skeleton
 * loading state instead of a spinner, so the layout doesn't jump once real
 * content arrives. */
export function Skeleton({ width = '100%', height = 16, radius = radii.sm, style }: { width?: number | `${number}%`; height?: number; radius?: number; style?: ViewProps['style'] }) {
  const opacity = useRef(new Animated.Value(0.4)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 1, duration: 650, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 0.4, duration: 650, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);
  return (
    <Animated.View
      style={[
        { width, height, borderRadius: radius, backgroundColor: colors.border, opacity },
        style,
      ]}
    />
  );
}

/** Skeleton shape matching a standard list `Card` — drop a few of these in
 * while a list is loading instead of a bare spinner. */
export function CardSkeleton() {
  return (
    <Card>
      <Skeleton width="60%" height={16} style={{ marginBottom: 10 }} />
      <Skeleton width="90%" height={12} style={{ marginBottom: 8 }} />
      <Skeleton width="40%" height={12} />
    </Card>
  );
}

interface ScreenContainerProps extends ViewProps {
  /** Screens with no native header (tab screens, auth screens) render flush
   * against the top of the physical screen — pass true so content clears the
   * notch/Dynamic Island/status bar instead of rendering underneath it.
   * Screens inside a Stack.Screen with a header already get that clearance
   * from the header itself, so this should stay false there. */
  safeTop?: boolean;
  /** Screens sitting directly above the bottom tab bar (or with no tab bar
   * at all) render flush against the bottom edge — pass true to clear the
   * home indicator / rounded corners at the bottom of the screen. */
  safeBottom?: boolean;
}

export function ScreenContainer({ style, children, safeTop, safeBottom, ...rest }: ScreenContainerProps) {
  const insets = useSafeAreaInsets();
  const t = themed();
  // Some browsers (Safari PWA/standalone in particular) report 0 for the
  // safe-area inset until the page has fully settled, or omit it entirely —
  // a fixed minimum cushion keeps content clear of the curved screen edges
  // either way, on top of whatever real inset is available.
  const topPadding = Math.max(insets.top, minSafeArea.top) + 16;
  const bottomPadding = Math.max(insets.bottom, minSafeArea.bottom) + 16;
  return (
    <View
      style={[
        styles.screen,
        t.screen,
        safeTop && { paddingTop: topPadding },
        safeBottom && { paddingBottom: bottomPadding },
        style,
      ]}
      {...rest}
    >
      <View style={styles.screenInner}>{children}</View>
    </View>
  );
}

export function FormInput(props: TextInputProps) {
  const t = themed();
  return <TextInput placeholderTextColor={colors.textFaint} style={[styles.input, t.input]} {...props} />;
}

export function Label({ children }: { children: string }) {
  const t = themed();
  return <Text style={[styles.label, t.label]}>{children}</Text>;
}

export function ErrorText({ children }: { children?: string | null }) {
  const t = themed();
  if (!children) return null;
  return <Text style={[styles.error, t.error]}>{children}</Text>;
}

export function EmptyState({ text, icon = 'file-tray-outline' }: { text: string; icon?: keyof typeof Ionicons.glyphMap }) {
  const t = themed();
  return (
    <View style={styles.emptyState}>
      <View style={[styles.emptyStateIconWrap, t.emptyStateIconWrap]}>
        <Ionicons name={icon} size={30} color={colors.textFaint} />
      </View>
      <Text style={[styles.emptyStateText, t.emptyStateText]}>{text}</Text>
    </View>
  );
}

/** Full-screen loading placeholder — use instead of `return null` while a
 * detail screen's data is still loading, so the user sees a spinner instead
 * of a flash of blank background. */
export function LoadingState() {
  return (
    <View style={styles.loadingState}>
      <ActivityIndicator size="large" color={colors.primary} />
    </View>
  );
}

/** Typography primitives — always use these instead of raw <Text> with an
 * inline fontWeight, so every screen actually renders in the loaded Inter
 * family (fontWeight alone falls back to the system font). */
export function Heading({ style, ...rest }: TextProps) {
  const t = themed();
  return <Text style={[styles.heading, t.heading, style]} {...rest} />;
}
export function Title({ style, ...rest }: TextProps) {
  const t = themed();
  return <Text style={[styles.title, t.title, style]} {...rest} />;
}
export function Body({ style, ...rest }: TextProps) {
  const t = themed();
  return <Text style={[styles.body, t.body, style]} {...rest} />;
}
export function Caption({ style, ...rest }: TextProps) {
  const t = themed();
  return <Text style={[styles.caption, t.caption, style]} {...rest} />;
}

export function Checkbox({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: ReactNode;
}) {
  const t = themed();
  return (
    <Pressable onPress={onToggle} style={[styles.checkboxRow, t.checkboxRow, checked && [styles.checkboxRowChecked, t.checkboxRowChecked]]}>
      <View style={[styles.checkboxBox, t.checkboxBox, checked && [styles.checkboxBoxChecked, t.checkboxBoxChecked]]}>
        {checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
      </View>
      <Text style={[styles.checkboxLabel, t.checkboxLabel]}>{label}</Text>
    </Pressable>
  );
}

interface ConfirmDialogProps {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Extra content rendered between the message and the buttons — e.g. a
   * password field for a step-up re-auth confirmation (see WalletScreen's
   * withdraw flow). */
  children?: ReactNode;
}

/** Modal-based confirmation — works on native AND web/PWA (unlike
 * `Alert.alert`, which has no web implementation). Use before any
 * irreversible action that moves money (approve/pay, withdraw, reject). */
export function ConfirmDialog({
  visible,
  title,
  message,
  confirmLabel = 'Xác nhận',
  cancelLabel = 'Huỷ',
  danger,
  loading,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  const t = themed();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => {
        // Without this guard, Android's hardware back button calls onCancel
        // directly, bypassing the disabled Cancel button below and letting
        // the user back out while the confirmed action is still in flight.
        if (!loading) onCancel();
      }}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalCard, t.modalCard]}>
          <Text style={[styles.modalTitle, t.modalTitle]}>{title}</Text>
          <Text style={[styles.modalMessage, t.modalMessage]}>{message}</Text>
          {children}
          <View style={{ marginTop: 18 }}>
            <Button
              title={confirmLabel}
              variant={danger ? 'danger' : 'primary'}
              onPress={onConfirm}
              loading={loading}
            />
            <Button title={cancelLabel} variant="outline" onPress={onCancel} disabled={loading} />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    padding: 16,
    ...(Platform.OS === 'web' ? { alignItems: 'center' } : null),
  },
  // On web the window can be much wider than a phone screen — cap content
  // to a phone-like width and center it instead of stretching edge to edge.
  screenInner:
    Platform.OS === 'web' ? { flex: 1, width: '100%', maxWidth: 480 } : { flex: 1, width: '100%' },
  card: {
    borderRadius: radii.lg,
    padding: 18,
    marginBottom: 12,
    borderWidth: 1,
    ...shadows.card,
  },
  gradientCard: {
    borderWidth: 0,
    shadowOpacity: 0.18,
  },
  button: {
    paddingVertical: 15,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  buttonOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    color: '#fff',
    fontFamily: fonts.semiBold,
    fontSize: 15.5,
    letterSpacing: 0.2,
    textAlign: 'center',
  },
  input: {
    borderWidth: 1.5,
    borderRadius: radii.md,
    paddingHorizontal: 15,
    paddingVertical: 13,
    marginBottom: 12,
    fontSize: 15,
    fontFamily: fonts.regular,
  },
  label: {
    fontSize: 12.5,
    fontFamily: fonts.semiBold,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  error: {
    marginBottom: 10,
    fontSize: 13,
    fontFamily: fonts.medium,
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
  },
  emptyStateIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: fonts.regular,
    textAlign: 'center',
  },
  loadingState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  heading: {
    fontSize: 24,
    fontFamily: fonts.bold,
    letterSpacing: -0.3,
  },
  title: {
    fontSize: 16,
    fontFamily: fonts.semiBold,
  },
  body: {
    fontSize: 14,
    fontFamily: fonts.regular,
  },
  caption: {
    fontSize: 12,
    fontFamily: fonts.medium,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 14,
    padding: 10,
    borderRadius: radii.sm,
    borderWidth: 1,
  },
  checkboxRowChecked: {},
  checkboxBox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    borderWidth: 1.5,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    marginTop: 1,
  },
  checkboxBoxChecked: {},
  checkboxMark: {
    color: '#fff',
    fontSize: 12,
    fontFamily: fonts.bold,
  },
  checkboxLabel: {
    flex: 1,
    fontSize: 13,
    fontFamily: fonts.regular,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: 400,
    borderRadius: radii.lg,
    padding: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: fonts.bold,
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    lineHeight: 20,
  },
});
